/******************************************************************
 * STRESS TEST ENGINE — Reuses the core policy loan calculator
 * with modified scenario variables for "what if" analysis.
 ******************************************************************/

import {
  calculateActualPolicyLoan,
  buildPlannedRepaymentSchedule,
  type PolicyLoanInput,
  type PolicyLoanPayment,
  type PolicyLoanResult,
  type PlannedRepaymentStrategyInput,
  type CombinedScenarioInput,
  addMonths,
  getNextPaymentDate,
} from "./calculationEngine";

// ── Types ──

export type StressScenarioType = "missed_payments" | "higher_rate" | "delayed_start" | "variable_rate";

export interface MissedPaymentsParams {
  numberOfMissedPayments: number;
  startingPaymentNumber: number; // 1-indexed
}

export interface HigherRateParams {
  newAnnualRate: number; // decimal, e.g. 0.08
  effectiveDate: string; // ISO date
}

export interface DelayedStartParams {
  delayPeriods: number; // number of payment periods to delay
}

export interface VariableRateStep {
  monthsFromStart: number;
  annualRate: number; // decimal
}

export interface VariableRateParams {
  label: string;
  steps: VariableRateStep[];
}

export interface StressScenarioInput {
  type: StressScenarioType;
  missedPayments?: MissedPaymentsParams;
  higherRate?: HigherRateParams;
  delayedStart?: DelayedStartParams;
  variableRate?: VariableRateParams;
}

export interface StressTestResult {
  scenarioLabel: string;
  stressedPolicyLoan: PolicyLoanResult;
  stressedPayments: PolicyLoanPayment[];
  projectedPayoffDate: string | null;
  totalPolicyLoanInterest: number;
  nextCapitalizationEstimate: number;
  totalIndebtednessAtEnd: number;
  // Deltas vs original
  extraInterest: number;
  payoffDelayDays: number;
  indebtednessIncrease: number;
}

// ── Helpers ──

function diffDaysBetween(a: string | null, b: string | null): number {
  if (!a || !b) return 0;
  const msPerDay = 86400000;
  return Math.round(
    (new Date(b + "T00:00:00").getTime() - new Date(a + "T00:00:00").getTime()) / msPerDay
  );
}

// ── Scenario runners ──

function runMissedPayments(
  baseInput: CombinedScenarioInput,
  basePayments: PolicyLoanPayment[],
  params: MissedPaymentsParams
): { payments: PolicyLoanPayment[]; label: string } {
  const startIdx = params.startingPaymentNumber - 1;
  const endIdx = startIdx + params.numberOfMissedPayments;
  const modified = basePayments.filter((_, i) => i < startIdx || i >= endIdx);
  return {
    payments: modified,
    label: `Missed ${params.numberOfMissedPayments} payment(s) starting at #${params.startingPaymentNumber}`,
  };
}

function runHigherRate(
  baseInput: CombinedScenarioInput,
  params: HigherRateParams
): { rateChanges: { id: string; effectiveDate: string; annualRate: number }[]; label: string } {
  return {
    rateChanges: [
      {
        id: "stress-rate-change",
        effectiveDate: params.effectiveDate,
        annualRate: params.newAnnualRate,
      },
    ],
    label: `Rate increases to ${(params.newAnnualRate * 100).toFixed(2)}% on ${params.effectiveDate}`,
  };
}

function runDelayedStart(
  baseInput: CombinedScenarioInput,
  basePayments: PolicyLoanPayment[],
  params: DelayedStartParams
): { payments: PolicyLoanPayment[]; label: string } {
  // Rebuild schedule with delayed start date
  const strategy = baseInput.repaymentStrategy;
  let delayedStart = strategy.startDate;
  for (let i = 0; i < params.delayPeriods; i++) {
    delayedStart = getNextPaymentDate(delayedStart, strategy.paymentFrequency);
  }
  const delayedPayments = buildPlannedRepaymentSchedule({
    ...strategy,
    startDate: delayedStart,
  });
  return {
    payments: delayedPayments,
    label: `Repayment delayed by ${params.delayPeriods} period(s)`,
  };
}

export function snapToNextAnniversary(
  fromDate: Date,
  anniversaryMonth: number,
  anniversaryDay: number
): string {
  const year = fromDate.getFullYear();
  let anniv = new Date(year, anniversaryMonth - 1, anniversaryDay);
  if (anniv <= fromDate) {
    anniv = new Date(year + 1, anniversaryMonth - 1, anniversaryDay);
  }
  // `anniv` is a LOCAL-midnight date; a UTC conversion (toISOString) shifts
  // the calendar day on any non-UTC machine. Format the local components.
  const mm = String(anniv.getMonth() + 1).padStart(2, "0");
  const dd = String(anniv.getDate()).padStart(2, "0");
  return `${anniv.getFullYear()}-${mm}-${dd}`;
}

function runVariableRate(
  baseInput: CombinedScenarioInput,
  params: VariableRateParams
): { rateChanges: { id: string; effectiveDate: string; annualRate: number }[]; label: string } {
  const loanStart = new Date(baseInput.policyLoanInput.policyLoanEffectiveDate + "T00:00:00");
  const annivMonth = baseInput.policyLoanInput.policyAnniversaryMonth;
  const annivDay = baseInput.policyLoanInput.policyAnniversaryDay;

  // For each step, calculate the raw date then snap to the next policy anniversary
  const rateChanges = params.steps.map((step, i) => {
    const rawDate = new Date(loanStart);
    rawDate.setMonth(rawDate.getMonth() + step.monthsFromStart);
    const effectiveDate = snapToNextAnniversary(rawDate, annivMonth, annivDay);
    return {
      id: `variable-rate-${i}`,
      effectiveDate,
      annualRate: step.annualRate,
    };
  });

  // Deduplicate: if multiple steps snap to the same anniversary, keep the last one
  const deduped = new Map<string, typeof rateChanges[0]>();
  for (const rc of rateChanges) {
    deduped.set(rc.effectiveDate, rc);
  }

  return { rateChanges: Array.from(deduped.values()), label: params.label };
}

// ── Main entry point ──

export function runStressTest(
  scenarioInput: CombinedScenarioInput,
  basePayments: PolicyLoanPayment[],
  baseResult: PolicyLoanResult,
  scenario: StressScenarioInput
): StressTestResult {
  let payments = [...basePayments];
  let rateChanges = [...(scenarioInput.policyLoanInput.rateChanges ?? [])];
  let label = "";

  switch (scenario.type) {
    case "missed_payments": {
      const r = runMissedPayments(scenarioInput, basePayments, scenario.missedPayments!);
      payments = r.payments;
      label = r.label;
      break;
    }
    case "higher_rate": {
      const r = runHigherRate(scenarioInput, scenario.higherRate!);
      rateChanges = [...rateChanges, ...r.rateChanges];
      label = r.label;
      break;
    }
    case "delayed_start": {
      const r = runDelayedStart(scenarioInput, basePayments, scenario.delayedStart!);
      payments = r.payments;
      label = r.label;
      break;
    }
    case "variable_rate": {
      const r = runVariableRate(scenarioInput, scenario.variableRate!);
      rateChanges = [...rateChanges, ...r.rateChanges];
      label = r.label;
      break;
    }
  }

  // Determine projection end from the last payment or the base projection
  const lastPaymentDate = payments.length > 0 ? payments[payments.length - 1].date : undefined;
  const baseLastDate = basePayments.length > 0 ? basePayments[basePayments.length - 1].date : undefined;
  const projectionEndDate = lastPaymentDate && baseLastDate
    ? (lastPaymentDate > baseLastDate ? lastPaymentDate : baseLastDate)
    : lastPaymentDate || baseLastDate;

  const fullInput: PolicyLoanInput = {
    ...scenarioInput.policyLoanInput,
    payments,
    rateChanges,
    projectionEndDate,
  };

  const stressedResult = calculateActualPolicyLoan(fullInput);

  const extraInterest =
    stressedResult.totalInterestAccruedToProjectionEnd -
    baseResult.totalInterestAccruedToProjectionEnd;

  const payoffDelayDays = diffDaysBetween(
    baseResult.estimatedPayoffDate,
    stressedResult.estimatedPayoffDate
  );

  const indebtednessIncrease =
    stressedResult.currentTotalIndebtedness - baseResult.currentTotalIndebtedness;

  return {
    scenarioLabel: label,
    stressedPolicyLoan: stressedResult,
    stressedPayments: payments,
    projectedPayoffDate: stressedResult.estimatedPayoffDate,
    totalPolicyLoanInterest: stressedResult.totalInterestAccruedToProjectionEnd,
    nextCapitalizationEstimate: stressedResult.nextCapitalizationEstimate,
    totalIndebtednessAtEnd: stressedResult.currentTotalIndebtedness,
    extraInterest,
    payoffDelayDays,
    indebtednessIncrease,
  };
}
